import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

/**
 * Represents extra attributes for tracking note creation events for contextual creation
 * These attributes should be populated based on the content from which the note is being created
 */
export type NoteCreationContextualAttributes = {
	/**
	 * The linked content's content Id
	 */
	noteLinkedObjectId?: string;
	/**
	 * The linked content's content type (currently supporting creation from pages and live docs)
	 */
	noteLinkedContentType?: 'page' | 'live';
	/**
	 * The linked content's space Id
	 */
	noteLinkedContainerId?: string;
};

export type NotesCreationAnalyticsEventParams = {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	noteId: string;
	entryPoint: 'noteList' | 'contextualCreate' | 'createNoteShortcut';
	routeName?: string;
	contextualAttributes?: NoteCreationContextualAttributes;
};

// This track event is shared between web and mobile, so any changes to the event structure
// must maintain parity between the two
export const triggerCreationTrackEvent = ({
	createAnalyticsEvent,
	noteId,
	entryPoint,
	contextualAttributes,
}: Omit<NotesCreationAnalyticsEventParams, 'routeName'>) => {
	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			source: entryPoint,
			actionSubject: 'note',
			actionSubjectId: noteId,
			action: 'created',
			attributes: {
				...contextualAttributes,
				flow: entryPoint,
			},
		},
	}).fire();
};

export const triggerCreationUIButtonClickEvent = ({
	createAnalyticsEvent,
	entryPoint,
	routeName,
	contextualAttributes,
}: Omit<NotesCreationAnalyticsEventParams, 'noteId'>) => {
	createAnalyticsEvent({
		type: 'sendUIEvent',
		data: {
			source: entryPoint,
			actionSubject: 'button',
			actionSubjectId: 'createNote',
			action: 'clicked',
			attributes: {
				...contextualAttributes,
				routeName,
			},
		},
	}).fire();
};
